



























































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import heic2any from "heic2any";
import BackOfNationalSvg from "@/modules/national/component/BackOfNationalSvg.vue";
import BackOfNationalDesktopSvg from "@/modules/national/component/BackOfNationalDesktopSvg.vue";
import PreviousPageArrow from "@/components/PreviousPageArrow.vue";
import { base64toBlob } from "@/utils/index";
import Loader from "@/components/svg/Loader.vue";
import { namespace } from "vuex-class";
import {
  DocumentType,
  DocumentSide,
  UploadPayload,
} from "@/modules/document/types";
import { NationalIdType } from "../types";
import axios from "axios";

const Document = namespace("document");
const Trn = namespace("trn");
const NationalId = namespace("nationalId");

@Component({
  metaInfo() {
    return { title: "Orba One | Back of National ID" };
  },
  components: {
    "back-of-national-svg": BackOfNationalSvg,
    "back-of-national-desktop-svg": BackOfNationalDesktopSvg,
    "previous-page-arrow": PreviousPageArrow,
    loader: Loader,
  },
})
export default class CaptureBackNationalId extends Vue {
  @Document.Action("upload") upload!: (payload: UploadPayload) => Promise<{}>;
  @Trn.Getter("getTrn") trn!: string;
  @Document.Getter("getCountryCode") countryCode!: string;
  @NationalId.Getter("getNationalIdType") nationalIdType!: NationalIdType;
  @NationalId.Getter("getNationalIdFrontBlob") nationalIdFrontBlob!: Blob;
  @NationalId.Getter("getNationalIdBackBlob") nationalIdBackBlob!: Blob;
  @NationalId.Action("setNationalIdBackBlob") setNationalIdBackBlob!: (
    payload: Blob
  ) => void;

  @Ref() previewDocument!: HTMLImageElement;

  base64Image = "";
  isImageLoaded = false;
  isUploading = false;
  isValidationError = false;
  imageBlob?: Blob;
  errorMessage = "";

  async previewImage(files: File[]) {
    const file = files?.[0];
    if (!file) return;

    // 🚫 Prevent PDFs
    if (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    ) {
      this.showErrorToast(
        "PDF files are not supported. Please upload an image (JPG, PNG, HEIC)."
      );
      return;
    }

    try {
      let blobToPreview: Blob = file;

      // ✅ Convert HEIC/HEIF to JPEG
      if (
        file.type === "image/heic" ||
        file.name.endsWith(".heic") ||
        file.type === "image/heif"
      ) {
        const converted = await heic2any({ blob: file, toType: "image/jpeg" });
        blobToPreview = converted as Blob;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event?.target?.result) {
          this.base64Image = event.target.result as string;
          this.imageBlob = await base64toBlob(this.base64Image);
          this.setNationalIdBackBlob(this.imageBlob);
          this.isImageLoaded = true;
        }
      };
      reader.readAsDataURL(blobToPreview);
    } catch (err) {
      console.error("Error loading image:", err);
      this.showErrorToast(
        "An error occurred while loading the image. Please try again."
      );
    }
  }

  retryImage() {
    this.isImageLoaded = false;
    this.isValidationError = false;
  }

  showErrorToast(message: string) {
    this.isValidationError = true;
    this.errorMessage = message;

    setTimeout(() => {
      this.isValidationError = false;
    }, 5000);
  }

  confirmImage() {
    if (this.nationalIdType === NationalIdType.NEW) {
      this.uploadNewNationalId();
    } else {
      this.uploadNationalBack();
    }
  }

  uploadNewNationalId() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");

    if (!publicKey || !applicantId) return;

    const formData = new FormData();
    formData.append("applicantId", applicantId);
    formData.append("publicKey", publicKey);
    formData.append("type", DocumentType.NATIONALID);
    formData.append("side", DocumentSide.DUAL);
    formData.append("TaxId", this.trn);
    formData.append("countryCode", this.countryCode);
    formData.append("backIsPrimary", "true");

    if (this.nationalIdFrontBlob && this.nationalIdBackBlob) {
      formData.append("file", this.nationalIdFrontBlob);
      formData.append("back", this.nationalIdBackBlob);
    }

    this.isUploading = true;
    this.upload(formData)
      .then(() => {
        this.isUploading = false;
        this.$router.push({ name: "finish", query: this.$route.query });
      })
      .catch(() => {
        this.isUploading = false;
        this.showErrorToast("Failed to upload National ID. Please try again.");
      });
  }

  uploadNationalBack() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");
    if (!publicKey || !applicantId) return;

    const formData = new FormData();
    formData.append("applicantId", applicantId);
    formData.append("publicKey", publicKey);
    formData.append("type", DocumentType.NATIONALID);
    formData.append("side", DocumentSide.BACK);
    formData.append("TaxId", this.trn);
    formData.append("countryCode", this.countryCode);

    if (this.imageBlob) {
      formData.append(
        "file",
        this.imageBlob,
        `national_back_${applicantId}_${publicKey}.png`
      );
    }

    this.isUploading = true;
    this.upload(formData)
      .then(() => {
        this.uploadNationalBackStep();
        this.isUploading = false;
        this.$router.push({ name: "finish", query: this.$route.query });
      })
      .catch(() => {
        this.isUploading = false;
        this.showErrorToast("Failed to validate ID. Please try again.");
      });
  }

  uploadNationalBackStep() {
    const params = new URLSearchParams(window.location.search);
    const applicantId = params.get("applicantId");
    const publicKey = params.get("publicKey");
    const magicLink = params.get("magic");
    const progressionStep = 17;

    if ((publicKey && applicantId) || magicLink) {
      axios.put(
        `${process.env.VUE_APP_API_URL}/applicants/${applicantId}/verification-progress-step`,
        { publicKey, progressionStep },
        { headers: { "Content-Type": "application/json" } }
      );
    }
  }

  isMobile(): boolean {
    return screen.width <= 760;
  }
}
